/**
 * useLiveMonitorTiles — v.3 wiring port of
 * `frontend-v2/src/hooks/useLiveMonitorTiles.ts` (v.1-D §7.13).
 *
 * Live Monitor's per-tile N+1 fix. v.1-C ran one
 * `getRecentValidationLogs` subscription per visible tile, which scaled
 * O(N) in the channel fleet. v.1-D issues a single fleet query —
 * `api.validationLogs.getRecentForRunningChannels` — and indexes results
 * client-side (NFR-004).
 *
 * v.3 port-site fix #3 (T106 §2.2 → T308 / plan §c''):
 *   The v.1-D reference SILENTLY fell back to `logs: []` whenever
 *   `getRecentForRunningChannels` returned `undefined` indefinitely — the
 *   exact "looks-done-but-isn't" pattern. `T314` lands the real backend
 *   body for `getRecentForRunningChannels`, after which the fallback path
 *   is impossible. Until then, this hook does NOT paint a hollow `logs:
 *   []`: it surfaces a structured `notWired: true` flag on each tile so
 *   the page can render the operator-honesty banner (NotWiredBanner) or
 *   degraded glyph rather than fabricating an "empty inbox" view of a
 *   surface that the backend has not yet wired.
 *
 *   The flag is the structural twin of `pendingIngestion: undefined` in
 *   `useNavBadges` — both surfaces resolve the v.1-D silent-fallback
 *   pattern with an honest signal the consumer must render, NOT with a
 *   placeholder zero / placeholder empty list.
 *
 * Loading: returns `undefined` while `useChannelList()` itself is still
 * resolving — there is nothing to render without the channel list.
 */
import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { useChannelList, type ChannelListRow } from './useChannelList';

export interface ValidationLog {
  channelId: string;
  eventType: string;
  timestamp: number;
  detail?: Record<string, unknown>;
}

export interface LiveMonitorTile extends ChannelListRow {
  /**
   * Recent validation logs for this channel, sliced from the single
   * fleet query. Empty array means "this channel is wired AND has no
   * recent logs in the window" — an HONEST empty.
   */
  logs: ValidationLog[];
  /**
   * `true` when the underlying `getRecentForRunningChannels` query has
   * not resolved (hollow / not-yet-wired). The page MUST render the
   * `NotWiredBanner` (or equivalent honesty glyph) when this is `true`
   * rather than treating `logs: []` as a real empty queue.
   *
   * v.3 port-site fix #3 — replaces the v.1-D silent-fallback that
   * returned `logs: []` for unwired tiles, which was indistinguishable
   * from a genuinely-empty real query.
   */
  notWired: boolean;
}

export function useLiveMonitorTiles(): LiveMonitorTile[] | undefined {
  const channels = useChannelList();
  const recentLogs = useQuery(
    api.validationLogs.getRecentForRunningChannels,
    {},
  ) as ValidationLog[] | undefined;

  return useMemo(() => {
    if (channels === undefined) return undefined;

    const visible = channels.filter(
      (c) => c.backendStatus === 'running' || c.backendStatus === 'ad_break',
    );

    if (recentLogs === undefined) {
      // Port-site fix #3 — surface `notWired: true` rather than the
      // v.1-D silent `logs: []` fallback. The page is responsible for
      // rendering the honesty banner in this branch.
      return visible.map<LiveMonitorTile>((c) => ({
        ...c,
        logs: [],
        notWired: true,
      }));
    }

    const logsByChannelId = new Map<string, ValidationLog[]>();
    for (const log of recentLogs) {
      const list = logsByChannelId.get(log.channelId) ?? [];
      list.push(log);
      logsByChannelId.set(log.channelId, list);
    }
    return visible.map<LiveMonitorTile>((c) => ({
      ...c,
      logs: logsByChannelId.get(c._id) ?? [],
      notWired: false,
    }));
  }, [channels, recentLogs]);
}
